import {WarningTheme} from 'common/components/warning/constants';
import {Translatable} from 'common/i18n';
import {REPORT_BASICS_LINKS, ReportName} from 'common/report/constants';
import {generateShortcutLabel, getCommandKey, getModifierLabel, isMacOS, KeyModifiers} from 'common/util/keyboard';

export enum WarningTrackingError {
  MissingMappedDatagroup = `missing-mapped-datagroup`,
  UnsupportedActionError = `unsupported-action`,
}

enum ExtraTheme {
  EmptyReport = `empty-report`,
}

export const WarningHeroTheme = {...WarningTheme, ...ExtraTheme};
export type WarningHeroTheme = WarningTheme | ExtraTheme;

export const WarningHeroReportName = {
  Dashboards: ReportName.Dashboards,
  Insights: ReportName.Insights,
  Impact: ReportName.Impact,
  Events: ReportName.Events,
  EventsSidebar: ReportName.EventsSidebar,
  Retention: ReportName.Retention,
  Funnels: ReportName.Funnels,
  Flows: ReportName.Flows,
  ExperimentReporting: ReportName.ExperimentReporting,
  Signal: ReportName.Signal,
  LookupTables: `lookup-tables`,
  PublicDashboards: `public-dashboards`,
  Users: `users`,
} as const;

export type WarningHeroReportName = (typeof WarningHeroReportName)[keyof typeof WarningHeroReportName];

export const WARNING_HERO_BASICS_LINKS = {
  ...REPORT_BASICS_LINKS,
  [WarningHeroReportName.LookupTables]: `https://help.mixpanel.com/hc/en-us/articles/360044139291-Lookup-tables`,
};

// TODO (Demo) - update the links to use redirect API
export const EXAMPLE_LINKS: Partial<{[key in WarningHeroReportName]: string}> = {
  [WarningHeroReportName.Dashboards]: `https://mixpanel.com/p/7QrYtba8YU2RP6ybsZSqRL`,
  [WarningHeroReportName.Insights]: `https://mixpanel.com/p/RebPqyeqAPMCEzcKEyj5t5`,
  [WarningHeroReportName.Retention]: `https://mixpanel.com/p/Co5qDd1SKzxyCoQAdn1Lnf`,
  [WarningHeroReportName.Funnels]: `https://mixpanel.com/p/PpNnEmndt8q4h893yFBrqn`,
  [WarningHeroReportName.Flows]: `https://mixpanel.com/p/3e2eNpXn8BTN7eMw1Xh2mU`,
  [WarningHeroReportName.LookupTables]: `https://www.youtube.com/watch?v=pzWpCH4jvJQ`,
};

export enum NoDataReason {
  Default = `default`,
  EmptyProject = `empty-project`,
  Timeout = `timeout`,
}
export const TIPS = [
  {title: `Did you know?`, body: `You can drag & drop a global filter into an event block.`},
  {
    title: `Adjust your report page layout.`,
    body: `On larger screen sizes you can click & drag vertically to resize the chart and data table.`,
  },
  {title: `Resize your board content.`, body: `Change a card’s height on a board by dragging from the bottom edge.`},
  {
    title: `Want to know who edited a board last?`,
    body: `Hover over a board's viewer count to see when it was last edited and by whom.`,
  },
  {title: `Did you know?`, body: `Public boards can be password-protected for additional security.`},
  {
    title: `Take your board public!`,
    body: `Public boards enable you to share a link that anyone can view, even if they don't have a Mixpanel account.`,
  },
  {
    title: `Want to know which teammates use Mixpanel the most?`,
    body: `Check the Mixpanel Usage section in Project Settings to identify the most active users.`,
  },
  {title: `Did you know?`, body: `You can adjust how a session is defined under Project Settings.`},
  {title: `Did you know?`, body: `When viewing a report, press the "4" key to hide the data table.`},
  {title: `Did you know?`, body: `When viewing a report, press the “3” key to hide the chart.`},
  {title: `Did you know?`, body: `When viewing a report, press the “1” key to collapse or expand the Query Panel.`},
  {
    title: `Customize your funnel reports.`,
    body: `Use the "Select Metrics" menu to choose which metrics to display in the data table when using a funnel steps chart.`,
  },
  {
    title: `Share the knowledge.`,
    body: `Share boards & reports to spread the knowledge and make data-driven decisions.`,
  },
  {
    title: `Did you know?`,
    body: `Data Views allow you to manage data access for a group of users within a single Mixpanel project.`,
  },
  {title: `Stay in the loop.`, body: `Use Email Digests to receive a weekly summary of a board in your inbox.`},
  {title: `Did you know?`, body: `Compare To Past allows you to see how your metrics are changing over time.`},
  {
    title: `Find downward trends before it’s too late.`,
    body: `Set an alert in Funnels or Insights to be notified when a certain threshold is reached.`,
  },
  {
    title: `Does your team use Slack?`,
    body: `The Mixpanel Slack integration allows you to easily preview reports in your Slack messages.`,
  },
  {
    title: `Need some inspiration?`,
    body: `Check out the "Mixpanel Demo Project" from the Help menu to view sample boards and reports.`,
  },
  {
    title: `Join our Community.`,
    body: `Click "Visit Community" in the Help menu to learn how others are using Mixpanel.`,
  },
  {
    title: `Want to know what we’ve shipped recently?`,
    body: `Take a look at “Changelog” in the Help menu to discover Mixpanel’s latest features.`,
  },
  {title: `Did you know?`, body: `You can plot your data on a logarithmic scale in Insights.`},
  {title: `Did you know?`, body: `Click a segment on an Insights chart to filter or exclude it from the report.`},
  {
    title: `Did you know?`,
    body: `You can click on any line, bar, or pie chart and select “View Users” to see the users who performed the event.`,
  },
  {title: `Did you know?`, body: `You can click & drag horizontally on a line chart to zoom in.`},
  {title: `Don’t be a stranger!`, body: `Add a picture to your profile under User Settings.`},
  {title: `Misname your event?`, body: `Change the display name of your events in Lexicon for improved clarity.`},
  {title: `Did you know?`, body: `Adding descriptions to your events makes them easier to identify.`},
  {title: `View a certain board often?`, body: `Add boards to your Favorites to quickly access them from the sidebar.`},
  {title: `Does it spark joy?`, body: `Marie Kondo your project data by marking unused events as “Hidden” in Lexicon.`},
  {title: `Did you know?`, body: `You can improve the loading time of a line chart by using larger time intervals.`},
  {title: `Did you know?`, body: `You can improve the loading time of a report by reducing the date range.`},
  {
    title: `Search from anywhere.`,
    body: `Use **${generateShortcutLabel(`K`, {
      modifiers: [getCommandKey()],
      joinStr: ` + `,
    })}** to quickly browse your boards and reports from any page.`,
  },
  {title: `Are your funnel steps out of order?`, body: `Drag & drop your events to rearrange steps in a funnel.`},
  // eslint-disable-next-line no-irregular-whitespace
  {title: `Did you know?`, body: `Mixpanel supports emojis! Give your reports and boards some personality 📊 🎉`},
  {title: `Did you know?`, body: `You can switch between dark and light mode at any time from the Settings menu.`},
  {
    title: `Want to add a link to a board?`,
    body: `Highlight some text & paste a URL to quickly create a link in a text card.`,
  },
  {
    title: `Did you know?`,
    body: `You can double-click the Query Panel’s right-border to snap it back to the default size.`,
  },
  {title: `Did you know?`, body: `You can use Custom Properties to combine multiple properties into one.`},
  {
    title: `Did you know?`,
    body: `You can drag & drop the boards in the Favorites section of the sidebar to rearrange their order.`,
  },
  {
    title: `Have a board you want everyone to see?`,
    body: `You can pin a board to the sidebar of your project, making it visible to all users.`,
  },
  {
    title: `Need to fix something?`,
    body: `Undo and redo changes with **${generateShortcutLabel(`Z`, {
      modifiers: [getCommandKey()],
      joinStr: ` + `,
    })}** and **${generateShortcutLabel(`Z`, {modifiers: [getCommandKey(), KeyModifiers.Shift], joinStr: ` + `})}**.`,
  },
  {title: `Did you know?`, body: `You can drag & drop query blocks between the filter and breakdown sections.`},
  {
    title: `Did you know?`,
    body: `You can quickly duplicate a query block by holding ${isMacOS() ? `**Option** (` : ``}**${getModifierLabel(
      KeyModifiers.Alt,
    )}** ${isMacOS() ? `)` : ``}and dragging it.`,
  },
];

export const EMPTY_REPORT_HEADER: Partial<Record<WarningHeroReportName, Translatable>> = {
  [WarningHeroReportName.Dashboards]: ({t}) => t(`boards.empty.header`),
  [WarningHeroReportName.Insights]: ({t}) => t(`insights.empty.header`),
  [WarningHeroReportName.Flows]: ({t}) => t(`flows.empty.header`),
  [WarningHeroReportName.Funnels]: ({t}) => t(`funnels.empty.header`),
  [WarningHeroReportName.Retention]: ({t}) => t(`retention.empty.header`),
  [WarningHeroReportName.Impact]: ({t}) => t(`impact.empty.header`),
  [WarningHeroReportName.ExperimentReporting]: ({t}) => t(`experiment-reporting.empty.header`),
  [WarningHeroReportName.LookupTables]: ({t}) => t(`lookup-tables.empty.header`),
  [WarningHeroReportName.PublicDashboards]: ({t}) => t(`public-boards.empty.header`),
  [WarningHeroReportName.Signal]: ({t}) => t(`signal.empty.header`),
};

export enum ReportStatus {
  // empty report with all default values
  Initial = `initial`,
  Empty = `empty`,
  Loading = `loading`,
  NoData = `no-data`,
  Error = `error`,
  Success = `success`, // report has successfully loaded data without any issues
}

export enum TemplateEntryPointPath {
  NewDashboard = `Clicked New Dashboard`,
  FirstDashboard = `First Dashboard`,
}
